import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useScriptSettings } from "@/contexts/ScriptSettingsContext";
import { useI18n } from "@/contexts/I18nContext";
import LanguagePicker from "@/components/LanguagePicker";
import { BRAND_NAME, BRAND_TAGLINE } from "@/lib/brand";
import NotificationBell from "@/components/NotificationBell";
import { Flame, LogOut, User, Shield, Settings as SettingsIcon, Globe, UserCircle, Menu, X, Users, Languages, PlayCircle } from "lucide-react";
import { useFontSize } from "@/contexts/FontSizeContext";
import { useIsSchoolStaff, schoolLinkLabel } from "@/components/school/SchoolHub";
import { schoolApi } from "@/lib/school";


export default function Header() {
  const { user, signOut } = useAuth();
  const { script, setScript } = useScriptSettings();
  const { lang, t, languages } = useI18n();
  const { increase, decrease, atMin, atMax } = useFontSize();
  const [langOpen, setLangOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);
  const nav = useNavigate();
  // Staff = anyone with the full admin-shell nav (admin, support, teacher).
  // Translator is deliberately NOT staff — it's a Student in every respect
  // (same nav, same streak, same brand-link destination) PLUS one extra
  // Translations link/badge layered on top, never a substitute for the
  // student experience.
  const isStaff = ["admin", "support", "mentor"].includes(user?.role);
  // School principals and teachers also get a "School" page (a read-only view of their school, shared by every app).
  const { staff: isSchoolStaff, role: schoolRole } = useIsSchoolStaff(schoolApi, !!user);
  const isAdmin = user?.role === "admin";
  const isTranslator = user?.role === "translator";
  const langEntry = languages.find((l) => l.code === lang) || languages[0];

  const mobileNavCls = "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-[#4B5563] hover:bg-[#F9F5EE] hover:text-[#064E3B] transition";

  // `safe-header` pads past the iOS status bar / Dynamic Island — without it
  // the toolbar below renders underneath the clock and notification banners
  // in the installed PWA, where the controls can't be tapped. See the
  // safe-area block in index.css.
  return (
    <header data-testid="app-header" className="sticky top-0 z-40 bg-white/75 backdrop-blur-xl border-b border-[#E7E0D2] safe-header">
      {/* max-w-[1280px] (not Tailwind's rem-based max-w-7xl) on purpose:
          Urdu/Tamil UI text gets a deliberate root-font-size boost
          (App.js's FontSizeDomSync, 1.3x/1.15x) so Nastaliq/Kalyani read at
          a normal size — but that boost resolves against the same rem unit
          every Tailwind width class uses, so max-w-7xl would inflate right
          along with it (80rem -> ~104rem, i.e. 1280px -> ~1664px) and stop
          constraining the header on anything but the widest screens. A
          fixed px cap keeps this row's width tied to the viewport, not to
          whatever the current font-size boost happens to be. */}
      <div className="max-w-[1280px] mx-auto px-3 min-[420px]:px-5 sm:px-8 min-h-16 py-2 flex items-center justify-between gap-2">
        {/* Left: Logo */}
        {/* Logo always goes to the landing page, signed in or not — "/"
            itself would just bounce a signed-in user straight to /dashboard
            (see RootRoute), so this points at the dedicated unconditional
            /landing route instead. */}
        <Link to="/landing" data-testid="brand-link" className="flex items-center gap-1 shrink-0 min-w-0">
          <img src="/Hikma-Verse-Logo.png" alt="Hikmah Verses" className="w-9 h-9 rounded-xl object-contain shrink-0" />
          <div className="leading-none">
            <div className="font-heading font-semibold text-[15px] text-[#064E3B] whitespace-nowrap" dir="ltr">{BRAND_NAME}</div>
            {/* Hidden on the narrowest phones: with the tagline the logo is wider than the toolbar beside it can spare, and the
                profile button ends up cut off at the screen edge. */}
            <div className="max-[419px]:hidden text-[10px] uppercase tracking-[0.05em] text-[#4B5563] whitespace-nowrap" dir="ltr">{BRAND_TAGLINE}</div>
          </div>
        </Link>

        {/* Center: Navigation — flex-1 so it takes whatever width is left
            between the logo and the right-side toolbar (never a fixed
            width), and flex-wrap so that if a language's labels genuinely
            don't fit on one line at the current viewport, whole nav items
            (never mid-label text) flow onto a second line inside this box
            instead of overflowing into the logo or the toolbar. Each link
            keeps `whitespace-nowrap` so a single label is never split
            across lines — only entire items wrap. */}
        <div className="hidden md:flex flex-1 justify-center items-center px-3 lg:px-4 min-w-0">
          <nav className="flex flex-wrap items-center justify-center gap-x-3 lg:gap-x-4 xl:gap-x-5 2xl:gap-x-6 gap-y-1 text-sm text-[#4B5563] font-medium">
            {!isStaff && (
              <>
                {user && (
                  <>
                    <Link data-testid="nav-dashboard" to="/dashboard" className="hover:text-[#064E3B] whitespace-nowrap">{t("nav.dashboard")}</Link>
                    <Link data-testid="nav-modules" to="/modules" className="hover:text-[#064E3B] whitespace-nowrap">{t("nav.modules")}</Link>
                    {/* Learning Programs / Certificate dropped from the nav — both
                        are already one click away from the Dashboard itself
                        (Learning Journey card / Three-Layer Assessment cards),
                        so keeping them here too was redundant. Routes/pages are
                        untouched, just no longer linked from the header. */}
                    <Link data-testid="nav-community" to="/community" className="hover:text-[#064E3B] inline-flex items-center gap-1 whitespace-nowrap">
                      <Users className="w-3.5 h-3.5" /> {t("nav.community")}
                    </Link>
                  </>
                )}
                {/* How To Use is public — its own route has no ProtectedRoute
                    gate (see App.js), the page itself is static video content
                    with no user-specific data, and a signed-out visitor
                    should be able to see how the app works before creating
                    an account. Dashboard/Modules/Community above stay
                    signed-in-only since they need a real account's data. */}
                <Link data-testid="nav-how-to-use" to="/how-to-use" className="hover:text-[#064E3B] inline-flex items-center gap-1 whitespace-nowrap">
                  <PlayCircle className="w-3.5 h-3.5" /> {t("nav.how_to_use")}
                </Link>
                {/* Immediately after How To Use, per the requested order.
                    Public like How To Use above it — the page itself is
                    reachable by a signed-out visitor too; the submit
                    action stays auth-gated (ProtectedRoute on the route,
                    see App.js) and login bounces back here via
                    `state.from` (see Login.jsx's `completeLogin`), so
                    clicking this while signed out just detours through
                    sign-in/sign-up before landing on the form. */}
                <Link data-testid="nav-apply-translator" to="/apply-translator" className="hover:text-[#064E3B] inline-flex items-center gap-1 whitespace-nowrap">
                  <Languages className="w-3.5 h-3.5" /> {t("nav.apply_translator")}
                </Link>
              </>
            )}
            {isStaff && (
              <>
                <Link data-testid="nav-admin-users" to="/admin/users" className="hover:text-[#064E3B] whitespace-nowrap">{t("nav.admin_users")}</Link>
                <Link data-testid="nav-admin-recitations" to="/admin/recitations" className="hover:text-[#064E3B] whitespace-nowrap">{t("nav.admin_recitations")}</Link>
                {isAdmin && (
                  <>
                    <Link data-testid="nav-admin-certs" to="/admin/certificates" className="hover:text-[#064E3B] whitespace-nowrap">{t("nav.admin_certs")}</Link>
                    <Link data-testid="nav-admin-programs" to="/admin/programs" className="hover:text-[#064E3B] whitespace-nowrap">{t("nav.admin_programs")}</Link>
                    <Link data-testid="nav-admin-analytics" to="/admin/analytics" className="hover:text-[#064E3B] whitespace-nowrap">{t("nav.admin_analytics")}</Link>
                  </>
                )}
              </>
            )}
            {/* Translator already sees the full student nav above (it's not
                isStaff) — this is the one thing layered on top, never a
                replacement for it. */}
            {isSchoolStaff && (
              <Link data-testid="nav-school" to="/school" className="hover:text-[#064E3B] whitespace-nowrap">{schoolLinkLabel(schoolRole)}</Link>
            )}
            {isTranslator && (
              <Link data-testid="nav-admin-translations" to="/admin/translations" className="hover:text-[#064E3B] whitespace-nowrap">{t("nav.admin_translations")}</Link>
            )}
          </nav>
        </div>

        {/* Right: Toolbar — one grouped, consistently-spaced row (accessibility
            controls, language/script pickers, role badge, notifications, profile). */}
        <div className="flex items-center gap-1.5 lg:gap-2 xl:gap-2.5 2xl:gap-3 shrink-0">
          <div className="inline-flex items-center rounded-full bg-[#FDFBF7] border border-[#E7E0D2] overflow-hidden">
            <button
              onClick={decrease}
              disabled={atMin}
              title={t("header.decrease_font_size")}
              className="px-2 py-1 text-xs font-semibold text-[#4B5563] hover:bg-[#F0EAE0] hover:text-[#064E3B] disabled:opacity-30 disabled:cursor-not-allowed transition"
            >A−</button>
            <span className="w-px h-4 bg-[#E7E0D2]" />
            <button
              onClick={increase}
              disabled={atMax}
              title={t("header.increase_font_size")}
              className="px-2 py-1 text-xs font-semibold text-[#4B5563] hover:bg-[#F0EAE0] hover:text-[#064E3B] disabled:opacity-30 disabled:cursor-not-allowed transition"
            >A+</button>
          </div>

          {/* Language dropdown — compact globe pill, click toggles a panel
              rendering the full LanguagePicker. The native name is shown so
              non-English users can recognize their language instantly.
              Deliberately OUTSIDE the `user ?` branch below: a signed-out
              visitor on Landing/Login had no way to change language at all
              before this, even though setLang() already works without auth
              (I18nContext queues the pending choice and syncs it once they
              sign in) — this was a real gap, not an auth-gated feature. */}
          <div className="relative">
            <button
              data-testid="header-lang-toggle"
              onClick={() => setLangOpen((v) => !v)}
              title={`Language: ${langEntry.label}`}
              className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FDFBF7] border border-[#E7E0D2] hover:border-[#064E3B] text-xs font-medium text-[#4B5563] hover:text-[#064E3B] transition">
              <Globe className="w-3.5 h-3.5" />
              <span dir={langEntry.dir}>{langEntry.native}</span>
            </button>
            {langOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setLangOpen(false)} />
                {/* end-0 (inset-inline-end), not right-0: the header's own
                    toolbar mirrors under dir="rtl" — this button isn't
                    reliably near the physical right edge any more once
                    it does, so a hardcoded right-0 panel can run off the
                    opposite edge of the screen. end-0 tracks whichever
                    physical side the trigger actually ends up on. */}
                <div data-testid="header-lang-panel"
                  className="absolute end-0 mt-2 w-56 rounded-2xl bg-white border border-[#E7E0D2] shadow-[0_20px_50px_-15px_rgba(6,78,59,0.25)] z-40 overflow-hidden">
                  <LanguagePicker compact onChosen={() => setLangOpen(false)} />
                </div>
              </>
            )}
          </div>

          {/* Hamburger — mobile only, and deliberately OUTSIDE the `user ?`
              branch below: it's the only way a mobile visitor reaches the
              nav row at all (the desktop <nav> above is `hidden md:flex`),
              so a signed-out visitor needs it too to ever see the
              How To Use link on a phone-width screen. */}
          <button
            className="md:hidden w-9 h-9 rounded-full bg-[#FDFBF7] border border-[#E7E0D2] grid place-items-center hover:border-[#064E3B] transition"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-4 h-4 text-[#064E3B]" /> : <Menu className="w-4 h-4 text-[#4B5563]" />}
          </button>

          {user ? (
            <>
              {/* Script (Mushaf) toggle — same compact treatment, one-tap flip. */}
              <button
                data-testid="header-script-toggle"
                onClick={() => setScript(script === "uthmani" ? "indopak" : "uthmani")}
                title={`Script: ${script === "uthmani" ? "Uthmani" : "Indo-Pak"} · click to switch`}
                className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FDFBF7] border border-[#E7E0D2] hover:border-[#064E3B] text-[10px] uppercase tracking-[0.16em] font-semibold text-[#4B5563] hover:text-[#064E3B] transition">
                <span className="text-[#D97706]">A</span>
                <span className="hidden xl:inline">{script === "uthmani" ? "Uthmani" : "Indo-Pak"}</span>
                <span className="xl:hidden">{script === "uthmani" ? "Uth" : "I-P"}</span>
              </button>
              {!isAdmin && (
                <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#FFF7ED] border border-[#FED7AA]">
                  <Flame className="w-3.5 h-3.5 text-[#D97706]" />
                  <span className="text-xs font-semibold text-[#9A3412]" data-testid="header-streak">{user.streak || 0}</span>
                </div>
              )}
              {isAdmin && (
                <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#064E3B] text-white text-xs font-medium whitespace-nowrap">
                  <Shield className="w-3.5 h-3.5" /> {t("nav.admin_badge")}
                </span>
              )}
              {isTranslator && (
                <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#064E3B] text-white text-xs font-medium whitespace-nowrap">
                  <Languages className="w-3.5 h-3.5" /> {t("nav.translator_badge")}
                </span>
              )}

              <NotificationBell />

              <div
                className="relative"
                onMouseEnter={() => setUserMenuOpen(true)}
                onMouseLeave={() => setUserMenuOpen(false)}
              >
                <button className="w-9 h-9 rounded-full bg-[#FDFBF7] border border-[#E7E0D2] grid place-items-center overflow-hidden hover:border-[#064E3B] transition" title={user.name}>
                  {user.picture
                    ? <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                    : <User className="w-4 h-4 text-[#064E3B]" />}
                </button>
                {userMenuOpen && (
                  // end-0, not right-0 — same RTL-mirroring reason as the
                  // language panel above: this menu's own trigger (the
                  // avatar) can end up on either physical edge once the
                  // toolbar mirrors, and the panel needs to track that.
                  <div className="absolute end-0 top-full pt-2 w-64 z-50">
                  <div className="rounded-2xl bg-white border border-[#E7E0D2] shadow-[0_20px_50px_-15px_rgba(6,78,59,0.25)] overflow-hidden">
                    <div className="px-5 py-4 border-b border-[#E7E0D2]">
                      <p className="font-semibold text-[15px] text-[#064E3B] truncate">{user.name}</p>
                      {user.email && <p className="text-sm text-[#4B5563] truncate mt-0.5">{user.email}</p>}
                    </div>
                    <div className="py-1.5">
                      {isAdmin && (
                        <Link
                          to="/admin"
                          data-testid="nav-admin"
                          className="flex items-center gap-3 px-5 py-3 text-sm text-[#1F2937] hover:bg-[#F9F5EE] hover:text-[#064E3B] transition"
                        >
                          <Shield className="w-4 h-4 text-[#064E3B]" /> Admin
                        </Link>
                      )}
                      {isTranslator && (
                        <Link
                          to="/admin/translations"
                          data-testid="nav-admin"
                          className="flex items-center gap-3 px-5 py-3 text-sm text-[#1F2937] hover:bg-[#F9F5EE] hover:text-[#064E3B] transition"
                        >
                          <Languages className="w-4 h-4 text-[#064E3B]" /> {t("nav.admin_translations")}
                        </Link>
                      )}
                      <Link
                        to="/profile"
                        data-testid="nav-profile"
                        className="flex items-center gap-3 px-5 py-3 text-sm text-[#1F2937] hover:bg-[#F9F5EE] hover:text-[#064E3B] transition"
                      >
                        <UserCircle className="w-4 h-4 text-[#4B5563]" /> {t("nav.profile")}
                      </Link>
                      <Link
                        to="/settings"
                        data-testid="nav-settings"
                        className="flex items-center gap-3 px-5 py-3 text-sm text-[#1F2937] hover:bg-[#F9F5EE] hover:text-[#064E3B] transition"
                      >
                        <SettingsIcon className="w-4 h-4 text-[#4B5563]" /> {t("nav.settings") || "Settings"}
                      </Link>
                      <div className="mx-4 my-1 border-t border-[#E7E0D2]" />
                      <button
                        data-testid="logout-btn"
                        onClick={async () => { await signOut(); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-sm text-[#1F2937] hover:bg-[#F9F5EE] hover:text-[#064E3B] transition"
                      >
                        <LogOut className="w-4 h-4 text-[#4B5563]" /> {t("nav.sign_out")}
                      </button>
                    </div>
                  </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link data-testid="header-signin-btn" to="/login"
              className="px-4 py-2 rounded-full bg-[#064E3B] text-white text-sm font-medium hover:bg-[#047857] transition"
            >{t("nav.sign_in")}</Link>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-[#E7E0D2] bg-white/95 backdrop-blur-xl px-5 py-4 flex flex-col gap-1">
          {!isStaff && (
            <>
              {user && (
                <>
                  <Link to="/dashboard" onClick={closeMobile} className={mobileNavCls}>{t("nav.dashboard")}</Link>
                  <Link to="/modules" onClick={closeMobile} className={mobileNavCls}>{t("nav.modules")}</Link>
                  <Link to="/community" onClick={closeMobile} className={`${mobileNavCls} inline-flex`}>
                    <Users className="w-4 h-4" /> {t("nav.community")}
                  </Link>
                </>
              )}
              {/* Public, same reasoning as the desktop nav's nav-how-to-use link. */}
              <Link to="/how-to-use" onClick={closeMobile} className={`${mobileNavCls} inline-flex`}>
                <PlayCircle className="w-4 h-4" /> {t("nav.how_to_use")}
              </Link>
              <Link to="/apply-translator" onClick={closeMobile} className={`${mobileNavCls} inline-flex`}>
                <Languages className="w-4 h-4" /> {t("nav.apply_translator")}
              </Link>
            </>
          )}
          {isStaff && (
            <>
              <Link to="/admin/users" onClick={closeMobile} className={mobileNavCls}>{t("nav.admin_users")}</Link>
              <Link to="/admin/recitations" onClick={closeMobile} className={mobileNavCls}>{t("nav.admin_recitations")}</Link>
              {isAdmin && (
                <>
                  <Link to="/admin/certificates" onClick={closeMobile} className={mobileNavCls}>{t("nav.admin_certs")}</Link>
                  <Link to="/admin/programs" onClick={closeMobile} className={mobileNavCls}>{t("nav.admin_programs")}</Link>
                  <Link to="/admin/analytics" onClick={closeMobile} className={mobileNavCls}>{t("nav.admin_analytics")}</Link>
                </>
              )}
            </>
          )}
          {isSchoolStaff && (
            <Link to="/school" onClick={closeMobile} className={mobileNavCls}>{schoolLinkLabel(schoolRole)}</Link>
          )}
          {isTranslator && (
            <Link to="/admin/translations" onClick={closeMobile} className={mobileNavCls}>{t("nav.admin_translations")}</Link>
          )}

          {user && (
            <>
              <div className="my-2 border-t border-[#E7E0D2]" />
              <button
                onClick={async () => { closeMobile(); await signOut(); }}
                className={`${mobileNavCls} text-[#EF4444] hover:bg-red-50 hover:text-[#DC2626]`}
              >
                <LogOut className="w-4 h-4" /> {t("nav.sign_out")}
              </button>
            </>
          )}
        </div>
      )}
    </header>
  );
}
